/**
 * Local track projects in IndexedDB. Stored documents are re-validated on the
 * way out like any import (storage can be edited or corrupted), so nothing
 * from the database reaches the compiler unchecked.
 *
 * Stores:
 *  - projects: { id, title, createdAt, updatedAt, json, completed? }
 *  - recovery: { id, savedAt, json }, the latest unsaved draft of a project,
 *    written shortly after every edit and cleared when the project is saved.
 */
import type { TrackDocument } from '../authoring/TrackDocument';
import { serializeTrack } from '../authoring/validate';
import { importTrackText } from './importExport';
import { titleWithSuffix } from './model';

export const DB_NAME = 'xernous-editor';
const DB_VERSION = 1;

export interface ProjectMeta {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  /** Fingerprint and time of the latest finish from the real spawn in a playtest. */
  completed?: { fingerprint: string; seconds: number; at: number };
}

interface ProjectRow extends ProjectMeta {
  json: string;
}

interface RecoveryRow {
  id: string;
  savedAt: number;
  json: string;
}

export type StorageErrorKind = 'unavailable' | 'quota' | 'corrupt' | 'missing' | 'failed';

export class StorageError extends Error {
  constructor(
    readonly kind: StorageErrorKind,
    message: string,
  ) {
    super(message);
    this.name = 'StorageError';
  }
}

function wrap(err: unknown, what: string): StorageError {
  if (err instanceof StorageError) return err;
  const name = err instanceof DOMException ? err.name : '';
  if (name === 'QuotaExceededError') return new StorageError('quota', `${what}: the browser's storage for this site is full. Export your track to a file, then delete old projects.`);
  return new StorageError('failed', `${what}: ${err instanceof Error ? err.message : String(err)}`);
}

const req = <T>(r: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error ?? new Error('IndexedDB request failed'));
  });

const done = (tx: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
  });

const meta = (r: ProjectRow): ProjectMeta => ({ id: r.id, title: r.title, createdAt: r.createdAt, updatedAt: r.updatedAt, ...(r.completed ? { completed: r.completed } : {}) });

export class ProjectStore {
  private constructor(private readonly db: IDBDatabase) {}

  static async open(factory: IDBFactory | null = globalThis.indexedDB ?? null, name = DB_NAME): Promise<ProjectStore> {
    if (!factory) throw new StorageError('unavailable', 'This browser has no IndexedDB (private mode may disable it). Tracks can still be exported to files.');
    try {
      const r = factory.open(name, DB_VERSION);
      r.onupgradeneeded = () => {
        const db = r.result;
        if (!db.objectStoreNames.contains('projects')) db.createObjectStore('projects', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('recovery')) db.createObjectStore('recovery', { keyPath: 'id' });
      };
      return new ProjectStore(await req(r));
    } catch (err: unknown) {
      throw new StorageError('unavailable', `Could not open local storage: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  close(): void {
    this.db.close();
  }

  async list(): Promise<ProjectMeta[]> {
    try {
      const rows = await req(this.db.transaction('projects').objectStore('projects').getAll() as IDBRequest<ProjectRow[]>);
      return rows.map(meta).sort((a, b) => b.updatedAt - a.updatedAt);
    } catch (err: unknown) {
      throw wrap(err, 'Listing projects failed');
    }
  }

  /** Loads and re-validates a project. */
  async load(id: string): Promise<{ meta: ProjectMeta; doc: TrackDocument }> {
    let row: ProjectRow | undefined;
    try {
      row = await req(this.db.transaction('projects').objectStore('projects').get(id) as IDBRequest<ProjectRow | undefined>);
    } catch (err: unknown) {
      throw wrap(err, 'Loading the project failed');
    }
    if (!row) throw new StorageError('missing', `There is no saved project "${id}".`);
    // The import path, so a project saved by an older format version is upgraded, not reported as damaged.
    const parsed = importTrackText(row.json);
    if (!parsed.ok) throw new StorageError('corrupt', `The saved project "${row.title}" is damaged: ${parsed.errors.slice(0, 3).join('; ')}`);
    return { meta: meta(row), doc: parsed.doc };
  }

  /** Saves the document as project `doc.id` (creating it when new). */
  async save(doc: TrackDocument, now = Date.now()): Promise<ProjectMeta> {
    try {
      const tx = this.db.transaction(['projects', 'recovery'], 'readwrite');
      const store = tx.objectStore('projects');
      const prev = await req(store.get(doc.id) as IDBRequest<ProjectRow | undefined>);
      const row: ProjectRow = {
        id: doc.id,
        title: doc.title,
        createdAt: prev?.createdAt ?? now,
        updatedAt: now,
        json: serializeTrack(doc),
        ...(prev?.completed ? { completed: prev.completed } : {}),
      };
      store.put(row);
      tx.objectStore('recovery').delete(doc.id);
      await done(tx);
      return meta(row);
    } catch (err: unknown) {
      throw wrap(err, 'Saving failed');
    }
  }

  async markCompleted(id: string, fingerprint: string, seconds: number, now = Date.now()): Promise<void> {
    try {
      const tx = this.db.transaction('projects', 'readwrite');
      const store = tx.objectStore('projects');
      const row = await req(store.get(id) as IDBRequest<ProjectRow | undefined>);
      if (row) store.put({ ...row, completed: { fingerprint, seconds, at: now } });
      await done(tx);
    } catch (err: unknown) {
      throw wrap(err, 'Recording the completed run failed');
    }
  }

  async rename(id: string, title: string): Promise<void> {
    const { doc } = await this.load(id);
    await this.save({ ...doc, title });
  }

  /** Copies a project under a new id. */
  async duplicate(id: string, newId: string, title?: string): Promise<ProjectMeta> {
    const { doc } = await this.load(id);
    return this.save({ ...doc, id: newId, title: title ?? titleWithSuffix(doc.title, ' copy') });
  }

  async delete(id: string): Promise<void> {
    try {
      const tx = this.db.transaction(['projects', 'recovery'], 'readwrite');
      tx.objectStore('projects').delete(id);
      tx.objectStore('recovery').delete(id);
      await done(tx);
    } catch (err: unknown) {
      throw wrap(err, 'Deleting failed');
    }
  }

  async putRecovery(doc: TrackDocument, now = Date.now()): Promise<void> {
    try {
      const tx = this.db.transaction('recovery', 'readwrite');
      tx.objectStore('recovery').put({ id: doc.id, savedAt: now, json: serializeTrack(doc) } satisfies RecoveryRow);
      await done(tx);
    } catch (err: unknown) {
      throw wrap(err, 'Writing the recovery copy failed');
    }
  }

  /** An unsaved draft newer than the saved project, if one survived (a crash or closed tab). */
  async recovery(id: string): Promise<{ savedAt: number; doc: TrackDocument } | null> {
    try {
      const tx = this.db.transaction(['recovery', 'projects']);
      const row = await req(tx.objectStore('recovery').get(id) as IDBRequest<RecoveryRow | undefined>);
      const project = await req(tx.objectStore('projects').get(id) as IDBRequest<ProjectRow | undefined>);
      if (!row || (project && project.updatedAt >= row.savedAt)) return null;
      const parsed = importTrackText(row.json);
      return parsed.ok ? { savedAt: row.savedAt, doc: parsed.doc } : null;
    } catch (err: unknown) {
      throw wrap(err, 'Reading the recovery copy failed');
    }
  }

  async clearRecovery(id: string): Promise<void> {
    try {
      const tx = this.db.transaction('recovery', 'readwrite');
      tx.objectStore('recovery').delete(id);
      await done(tx);
    } catch (err: unknown) {
      throw wrap(err, 'Clearing the recovery copy failed');
    }
  }

  /** Approximate usage, when the browser reports it. */
  static async usage(): Promise<{ usage: number; quota: number } | null> {
    const est = await globalThis.navigator?.storage?.estimate?.();
    return est && est.usage !== undefined && est.quota !== undefined ? { usage: est.usage, quota: est.quota } : null;
  }
}

/** A project id that sorts by creation and is safe as a document id. */
export function newProjectId(now = Date.now(), random: () => number = Math.random): string {
  return `track-${now.toString(36)}-${Math.floor(random() * 36 ** 4).toString(36).padStart(4, '0')}`;
}
